class EmeProductsMegaMenu extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.contentObserver?.disconnect();
    this.controller = new AbortController();
    this.cards = this.querySelector('[data-featured-cards]');
    this.label = this.querySelector('[data-featured-label]');
    this.defaultLabel ||= this.querySelector('[data-featured-default-label]')?.textContent.trim() || this.dataset.defaultLabel || this.label?.textContent.trim();
    this.currentTopic ||= 'default';
    this.lastOverviewChoice ||= new Map();
    this.generation = (this.generation || 0) + 1;
    const options = { signal: this.controller.signal };
    this.addEventListener('pointerover', (event) => {
      if (event.pointerType === 'touch') return;
      const control = this.directoryControl(event.target);
      if (!control) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.selectControl(control), 130);
    }, options);
    this.addEventListener('focusin', (event) => {
      const control = this.directoryControl(event.target);
      if (control) { clearTimeout(this.timer); this.selectControl(control); }
    }, options);
    this.addEventListener('pointerleave', () => clearTimeout(this.timer), options);
    this.addEventListener('toggle', (event) => {
      if (event.target instanceof HTMLDetailsElement && event.target.open) {
        const summary = event.target.querySelector(':scope > summary');
        const control = summary && this.directoryControl(summary);
        if (control) this.selectControl(control);
      }
    }, { ...options, capture: true });
    if (this.dataset.mode === 'desktop') {
      const disclosure = this.closest('.menu-list__list-item')?.querySelector('.menu-list__disclosure');
      if (disclosure) {
        this.observer = new MutationObserver(() => {
          if (disclosure.getAttribute('aria-expanded') === 'false') this.reset();
        });
        this.observer.observe(disclosure, { attributes: true, attributeFilter: ['aria-expanded'] });
      }
    } else {
      this.drawerDetails = this.closest('details');
      this.drawerDetails?.addEventListener('toggle', () => {
        if (!this.drawerDetails.open) this.reset();
      }, options);
    }
    this.contentObserver = new MutationObserver(() => {
      if (!this.isConnected || !this.lastRenderedSource || this.dataset.featuredSource === this.lastRenderedSource) return;
      // Native section refreshes can preserve this element while restoring server HTML.
      this.cards = this.querySelector('[data-featured-cards]');
      this.label = this.querySelector('[data-featured-label]');
      this.showTopic(this.requestedTopic || this.currentTopic, this.requestedLabel || this.defaultLabel, true);
    });
    this.contentObserver.observe(this, { attributes: true, attributeFilter: ['data-featured-source'] });
    this.showTopic(this.requestedTopic || this.currentTopic, this.requestedLabel || this.label?.textContent.trim() || this.defaultLabel, true);
  }
  directoryControl(target) {
    if (!(target instanceof Element)) return null;
    const control = target.closest('a[href], summary[id]');
    if (!control || !this.contains(control) || control.closest('.eme-products-mega__featured')) return null;
    if (this.dataset.mode === 'desktop' && !control.closest('.eme-products-mega__links')) return null;
    if (this.dataset.mode === 'drawer' && !control.closest('.menu-drawer__menu')) return null;
    return control;
  }
  topicForControl(control) {
    let groupIndex, leafIndex;
    if (this.dataset.mode === 'desktop') {
      const group = control.closest('.mega-menu__column');
      groupIndex = [...this.querySelectorAll('.eme-products-mega__links .mega-menu__column')].indexOf(group);
      if (groupIndex < 0) return null;
      if (control.classList.contains('mega-menu__link--parent')) return 'group-' + groupIndex;
      leafIndex = [...group.querySelectorAll('a:not(.mega-menu__link--parent)')].indexOf(control);
    } else {
      const groups = [...this.querySelectorAll('summary[id^="HeaderDrawer-products-"]')];
      const summary = control.matches('summary') ? control : control.closest('details')?.querySelector(':scope > summary');
      groupIndex = groups.indexOf(summary);
      if (groupIndex < 0) return null;
      if (control === summary) return 'group-' + groupIndex;
      leafIndex = [...summary.parentElement.querySelectorAll('a.menu-drawer__menu-item--child')].indexOf(control);
    }
    if (leafIndex < 0) return null;
    const topic = 'leaf-' + groupIndex + '-' + leafIndex;
    const template = this.querySelector('template[data-featured-topic="' + topic + '"]');
    if (!template || template.dataset.featuredUrl !== control.getAttribute('href')) return null;
    return topic;
  }
  selectControl(control) {
    const topic = this.topicForControl(control);
    if (!topic) return;
    this.querySelectorAll('[data-eme-active]').forEach((el) => el.removeAttribute('data-eme-active'));
    control.setAttribute('data-eme-active', '');
    this.showTopic(topic, control.textContent.trim());
  }
  overviewTemplate(topic) {
    if (topic !== 'default' && !topic.startsWith('group-')) return null;
    const prefix = topic === 'default' ? 'leaf-' : 'leaf-' + topic.slice('group-'.length) + '-';
    const candidates = [...this.querySelectorAll('template[data-featured-topic^="' + prefix + '"]')]
      .filter((template) => template.content.querySelectorAll('[data-featured-product]').length === 2);
    const previous = this.lastOverviewChoice.get(topic);
    const alternatives = candidates.filter((template) => template.dataset.featuredTopic !== previous);
    const pool = alternatives.length ? alternatives : candidates;
    return pool.length ? pool[Math.floor(Math.random() * pool.length)] : null;
  }
  async showTopic(topic, label, force = false) {
    if (!this.cards || !this.label) return;
    this.requestedTopic = topic;
    this.requestedLabel = label;
    const generation = ++this.generation;
    if (topic === this.currentTopic && !force) { this.label.textContent = label; return; }
    const template = this.overviewTemplate(topic) || this.querySelector('template[data-featured-topic="' + topic + '"]');
    if (!template) return;
    const content = template.content.cloneNode(true);
    const images = [...content.querySelectorAll('img')];
    images.forEach((img) => { img.loading = 'eager'; });
    await Promise.race([
      Promise.all(images.map((img) => img.decode().catch(() => {}))),
      new Promise((resolve) => setTimeout(resolve, 600))
    ]);
    if (generation !== this.generation || !this.isConnected) return;
    this.label.textContent = label;
    this.lastRenderedSource = template.dataset.featuredTopic;
    this.cards.replaceChildren(content);
    this.currentTopic = topic;
    this.dataset.featuredSource = template.dataset.featuredTopic;
    if (topic === 'default' || topic.startsWith('group-')) this.lastOverviewChoice.set(topic, template.dataset.featuredTopic);
  }
  reset() {
    clearTimeout(this.timer);
    this.querySelectorAll('[data-eme-active]').forEach((el) => el.removeAttribute('data-eme-active'));
    this.showTopic('default', this.defaultLabel, true);
  }
  disconnectedCallback() {
    clearTimeout(this.timer);
    this.generation++;
    this.controller?.abort();
    this.observer?.disconnect();
    this.contentObserver?.disconnect();
  }
}
if (!customElements.get('eme-products-mega-menu')) customElements.define('eme-products-mega-menu', EmeProductsMegaMenu);
